// 色の読み取りと書き方（段階 C の2 の 7g、2026-10-26。前は d3 v4.10.2 の d3.color・d3.rgb）。
// d3-color（Copyright 2010-2017 Mike Bostock, BSD-3-Clause）の読み方・書き方を TS に移したもの。
// 動きの最後の色（"rgb(r, g, b)"）・前任者の処理（charat.legacy.js の肌色の計算）の色の文字を前と同じにするため、読める形も同じにしている

/** rgb の色（d3.rgb と同じ。r・g・b は 0〜255 で端数も持つ、書くときに丸める） */
export class Rgb {
  r: number;
  g: number;
  b: number;
  opacity: number;
  constructor(r: unknown, g: unknown, b: unknown, opacity: unknown) {
    this.r = +(r as number);
    this.g = +(g as number);
    this.b = +(b as number);
    this.opacity = +(opacity as number);
  }
  rgb(): Rgb {
    return this;
  }
  displayable(): boolean {
    return 0 <= this.r && this.r <= 255 && 0 <= this.g && this.g <= 255 && 0 <= this.b && this.b <= 255 && 0 <= this.opacity && this.opacity <= 1;
  }
  toString(): string {
    let a = this.opacity;
    a = isNaN(a) ? 1 : Math.max(0, Math.min(1, a));
    return (a === 1 ? 'rgb(' : 'rgba(') + byte(this.r) + ', ' + byte(this.g) + ', ' + byte(this.b) + (a === 1 ? ')' : ', ' + a + ')');
  }
}
const byte = (v: number): number => Math.max(0, Math.min(255, Math.round(v) || 0));

/** hsl の色（読むときだけ。rgb に直して使う） */
class Hsl {
  constructor(
    public h: number,
    public s: number,
    public l: number,
    public opacity: number,
  ) {}
  rgb(): Rgb {
    const h = (this.h % 360) + +(this.h < 0) * 360,
      s = isNaN(h) || isNaN(this.s) ? 0 : this.s,
      l = this.l,
      m2 = l + (l < 0.5 ? l : 1 - l) * s,
      m1 = 2 * l - m2;
    return new Rgb(hsl2rgb(h >= 240 ? h - 240 : h + 120, m1, m2), hsl2rgb(h, m1, m2), hsl2rgb(h < 120 ? h + 240 : h - 120, m1, m2), this.opacity);
  }
}
const hsl2rgb = (h: number, m1: number, m2: number): number => (h < 60 ? m1 + ((m2 - m1) * h) / 60 : h < 180 ? m2 : h < 240 ? m1 + ((m2 - m1) * (240 - h)) / 60 : m1) * 255;

const INT = '\\s*([+-]?\\d+)\\s*',
  NUM = '\\s*([+-]?\\d*\\.?\\d+(?:[eE][+-]?\\d+)?)\\s*',
  PCT = '\\s*([+-]?\\d*\\.?\\d+(?:[eE][+-]?\\d+)?)%\\s*';
const reHex3 = /^#([0-9a-f]{3})$/,
  reHex6 = /^#([0-9a-f]{6})$/,
  reRgbInteger = new RegExp('^rgb\\(' + [INT, INT, INT] + '\\)$'),
  reRgbPercent = new RegExp('^rgb\\(' + [PCT, PCT, PCT] + '\\)$'),
  reRgbaInteger = new RegExp('^rgba\\(' + [INT, INT, INT, NUM] + '\\)$'),
  reRgbaPercent = new RegExp('^rgba\\(' + [PCT, PCT, PCT, NUM] + '\\)$'),
  reHslPercent = new RegExp('^hsl\\(' + [NUM, PCT, PCT] + '\\)$'),
  reHslaPercent = new RegExp('^hsla\\(' + [NUM, PCT, PCT, NUM] + '\\)$');

// CSS の色の名前（d3-color と同じ表）
const NAMED: Record<string, number> = {
  aliceblue: 0xf0f8ff, antiquewhite: 0xfaebd7, aqua: 0x00ffff, aquamarine: 0x7fffd4, azure: 0xf0ffff, beige: 0xf5f5dc, bisque: 0xffe4c4,
  black: 0x000000, blanchedalmond: 0xffebcd, blue: 0x0000ff, blueviolet: 0x8a2be2, brown: 0xa52a2a, burlywood: 0xdeb887, cadetblue: 0x5f9ea0,
  chartreuse: 0x7fff00, chocolate: 0xd2691e, coral: 0xff7f50, cornflowerblue: 0x6495ed, cornsilk: 0xfff8dc, crimson: 0xdc143c, cyan: 0x00ffff,
  darkblue: 0x00008b, darkcyan: 0x008b8b, darkgoldenrod: 0xb8860b, darkgray: 0xa9a9a9, darkgreen: 0x006400, darkgrey: 0xa9a9a9,
  darkkhaki: 0xbdb76b, darkmagenta: 0x8b008b, darkolivegreen: 0x556b2f, darkorange: 0xff8c00, darkorchid: 0x9932cc, darkred: 0x8b0000,
  darksalmon: 0xe9967a, darkseagreen: 0x8fbc8f, darkslateblue: 0x483d8b, darkslategray: 0x2f4f4f, darkslategrey: 0x2f4f4f,
  darkturquoise: 0x00ced1, darkviolet: 0x9400d3, deeppink: 0xff1493, deepskyblue: 0x00bfff, dimgray: 0x696969, dimgrey: 0x696969,
  dodgerblue: 0x1e90ff, firebrick: 0xb22222, floralwhite: 0xfffaf0, forestgreen: 0x228b22, fuchsia: 0xff00ff, gainsboro: 0xdcdcdc,
  ghostwhite: 0xf8f8ff, gold: 0xffd700, goldenrod: 0xdaa520, gray: 0x808080, green: 0x008000, greenyellow: 0xadff2f, grey: 0x808080,
  honeydew: 0xf0fff0, hotpink: 0xff69b4, indianred: 0xcd5c5c, indigo: 0x4b0082, ivory: 0xfffff0, khaki: 0xf0e68c, lavender: 0xe6e6fa,
  lavenderblush: 0xfff0f5, lawngreen: 0x7cfc00, lemonchiffon: 0xfffacd, lightblue: 0xadd8e6, lightcoral: 0xf08080, lightcyan: 0xe0ffff,
  lightgoldenrodyellow: 0xfafad2, lightgray: 0xd3d3d3, lightgreen: 0x90ee90, lightgrey: 0xd3d3d3, lightpink: 0xffb6c1, lightsalmon: 0xffa07a,
  lightseagreen: 0x20b2aa, lightskyblue: 0x87cefa, lightslategray: 0x778899, lightslategrey: 0x778899, lightsteelblue: 0xb0c4de,
  lightyellow: 0xffffe0, lime: 0x00ff00, limegreen: 0x32cd32, linen: 0xfaf0e6, magenta: 0xff00ff, maroon: 0x800000, mediumaquamarine: 0x66cdaa,
  mediumblue: 0x0000cd, mediumorchid: 0xba55d3, mediumpurple: 0x9370db, mediumseagreen: 0x3cb371, mediumslateblue: 0x7b68ee,
  mediumspringgreen: 0x00fa9a, mediumturquoise: 0x48d1cc, mediumvioletred: 0xc71585, midnightblue: 0x191970, mintcream: 0xf5fffa,
  mistyrose: 0xffe4e1, moccasin: 0xffe4b5, navajowhite: 0xffdead, navy: 0x000080, oldlace: 0xfdf5e6, olive: 0x808000, olivedrab: 0x6b8e23,
  orange: 0xffa500, orangered: 0xff4500, orchid: 0xda70d6, palegoldenrod: 0xeee8aa, palegreen: 0x98fb98, paleturquoise: 0xafeeee,
  palevioletred: 0xdb7093, papayawhip: 0xffefd5, peachpuff: 0xffdab9, peru: 0xcd853f, pink: 0xffc0cb, plum: 0xdda0dd, powderblue: 0xb0e0e6,
  purple: 0x800080, rebeccapurple: 0x663399, red: 0xff0000, rosybrown: 0xbc8f8f, royalblue: 0x4169e1, saddlebrown: 0x8b4513, salmon: 0xfa8072,
  sandybrown: 0xf4a460, seagreen: 0x2e8b57, seashell: 0xfff5ee, sienna: 0xa0522d, silver: 0xc0c0c0, skyblue: 0x87ceeb, slateblue: 0x6a5acd,
  slategray: 0x708090, slategrey: 0x708090, snow: 0xfffafa, springgreen: 0x00ff7f, steelblue: 0x4682b4, tan: 0xd2b48c, teal: 0x008080,
  thistle: 0xd8bfd8, tomato: 0xff6347, turquoise: 0x40e0d0, violet: 0xee82ee, wheat: 0xf5deb3, white: 0xffffff, whitesmoke: 0xf5f5f5,
  yellow: 0xffff00, yellowgreen: 0x9acd32,
};

const rgbn = (n: number): Rgb => new Rgb((n >> 16) & 255, (n >> 8) & 255, n & 255, 1);
const rgba = (r: unknown, g: unknown, b: unknown, a: unknown): Rgb => {
  if ((a as number) <= 0) r = g = b = NaN;
  return new Rgb(r, g, b, a);
};
const hsla = (h: unknown, s: number, l: number, a: unknown): Hsl => {
  let hh = +(h as number), ss = s;
  if ((a as number) <= 0) hh = ss = l = NaN;
  else if (l <= 0 || l >= 1) hh = ss = NaN;
  else if (s <= 0) hh = NaN;
  return new Hsl(hh, ss, l, +(a as number));
};

/** 色の文字を読む（d3.color。読めなければ null。hsl は rgb に直して返す） */
export function color(format: unknown): Rgb | null {
  const s = String(format).trim().toLowerCase();
  let m: RegExpExecArray | null;
  if ((m = reHex3.exec(s))) {
    const n = parseInt(m[1], 16);
    return new Rgb(((n >> 8) & 0xf) | ((n >> 4) & 0x0f0), ((n >> 4) & 0xf) | (n & 0xf0), ((n & 0xf) << 4) | (n & 0xf), 1);
  }
  if ((m = reHex6.exec(s))) return rgbn(parseInt(m[1], 16));
  if ((m = reRgbInteger.exec(s))) return new Rgb(m[1], m[2], m[3], 1);
  if ((m = reRgbPercent.exec(s))) return new Rgb((+m[1] * 255) / 100, (+m[2] * 255) / 100, (+m[3] * 255) / 100, 1);
  if ((m = reRgbaInteger.exec(s))) return rgba(m[1], m[2], m[3], m[4]);
  if ((m = reRgbaPercent.exec(s))) return rgba((+m[1] * 255) / 100, (+m[2] * 255) / 100, (+m[3] * 255) / 100, m[4]);
  if ((m = reHslPercent.exec(s))) return hsla(m[1], +m[2] / 100, +m[3] / 100, 1).rgb();
  if ((m = reHslaPercent.exec(s))) return hsla(m[1], +m[2] / 100, +m[3] / 100, m[4]).rgb();
  if (Object.prototype.hasOwnProperty.call(NAMED, s)) return rgbn(NAMED[s]);
  if (s === 'transparent') return new Rgb(NaN, NaN, NaN, 0);
  return null;
}

/** d3.rgb（色の文字・色から。読めなければ全部 NaN。r, g, b[, opacity] を渡せばその色） */
export function rgb(r: unknown, g?: unknown, b?: unknown, opacity?: unknown): Rgb {
  if (arguments.length === 1) {
    const c = r instanceof Rgb ? r : color(r);
    return c ? new Rgb(c.r, c.g, c.b, c.opacity) : new Rgb(NaN, NaN, NaN, NaN);
  }
  return new Rgb(r, g, b, opacity == null ? 1 : opacity);
}
